/**
 * Tiny DOM helpers - no framework. Keeps page modules from drowning in
 * `document.createElement` boilerplate.
 */

// Permissive function-type slot so callers can declare narrower handler
// signatures like `(e: PointerEvent) => void` and have them assignable here.
// Using `never` as the param type makes the type contravariantly compatible
// with any concrete event subtype (PointerEvent, MouseEvent, etc.). The
// runtime addEventListener call below doesn't inspect parameter types.
type AnyEventHandler = (event: never) => void
type Attrs = Record<string, string | number | boolean | AnyEventHandler | undefined>

export function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  attrs?: Attrs,
  children?: Array<Node | string | null | undefined> | string,
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag)
  if (attrs) {
    for (const [k, v] of Object.entries(attrs)) {
      if (v === undefined || v === false) continue
      if (k.startsWith('on') && typeof v === 'function') {
        node.addEventListener(k.slice(2).toLowerCase(), v as EventListener)
      } else if (k === 'className') {
        node.className = String(v)
      } else if (k === 'class') {
        node.className = String(v)
      } else if (v === true) {
        node.setAttribute(k, '')
      } else {
        node.setAttribute(k, String(v))
      }
    }
  }
  if (typeof children === 'string') {
    node.textContent = children
  } else if (children) {
    for (const c of children) {
      if (c === null || c === undefined) continue
      node.appendChild(typeof c === 'string' ? document.createTextNode(c) : c)
    }
  }
  return node
}

/**
 * Mount-defer machinery. Two situations rip the rug from under a user
 * interaction by replacing the DOM mid-gesture:
 *
 *   1. SLIDER DRAG: a range input fires `input` per-pixel; if we rerender
 *      synchronously the slider node is detached and pointer capture is
 *      lost, so the drag aborts the moment the user moves one pixel.
 *
 *   2. SELECT MENU OPENING: tapping a <select> on mobile opens a
 *      native OS menu. The OS attaches the menu to the live DOM node it
 *      sees at gesture start. A rerender between the tap and the menu
 *      paint replaces that node and the menu silently does nothing - the
 *      tester sees "the dropdown didn't open."
 *
 * Both are fixed the same way: while an interaction is in flight, defer
 * mount() and coalesce; flush once the interaction ends. We also rAF-batch
 * any rapid-fire mount() calls so a noisy 10Hz state poll collapses to
 * one paint per frame.
 *
 * Side-effect listeners are installed exactly once at module load. They
 * use capture-phase so they observe activity before any per-card handler
 * can stopPropagation.
 */
let sliderDragActive = false
let pendingMount: { parent: HTMLElement; view: Node } | null = null

function flushPending(opts: { force?: boolean } = {}): void {
  if (pendingMount === null) return
  if (opts.force !== true && shouldDefer()) return
  const next = pendingMount
  pendingMount = null
  mountImmediate(next.parent, next.view)
}

/** Defer while a slider is being dragged or a <select> is focused (its
 *  native menu may be open on mobile). */
function shouldDefer(): boolean {
  if (sliderDragActive) return true
  if (typeof document !== 'undefined') {
    const active = document.activeElement
    if (active !== null && active.tagName === 'SELECT') return true
  }
  return false
}

if (typeof document !== 'undefined') {
  document.addEventListener('pointerdown', (e: PointerEvent) => {
    const t = e.target as HTMLElement | null
    if (t instanceof HTMLInputElement && t.type === 'range') {
      sliderDragActive = true
    }
  }, true)
  const sliderRelease = (): void => {
    if (!sliderDragActive) return
    sliderDragActive = false
    flushPending()
  }
  document.addEventListener('pointerup', sliderRelease, true)
  document.addEventListener('pointercancel', sliderRelease, true)
  // When a <select> blurs (menu closed by selection or by tapping outside)
  // or a value is committed, force-flush any deferred view. By the time
  // either event fires the native menu has closed, so it's safe to
  // replace the node even though the select may still be the focused
  // element for a tick.
  document.addEventListener('blur', (e: FocusEvent) => {
    const t = e.target as HTMLElement | null
    if (t !== null && t.tagName === 'SELECT') flushPending({ force: true })
  }, true)
  document.addEventListener('change', (e: Event) => {
    const t = e.target as HTMLElement | null
    if (t !== null && t.tagName === 'SELECT') flushPending({ force: true })
  }, true)
}

export function mount(parent: HTMLElement, view: Node): void {
  if (shouldDefer()) {
    // Latest view wins; older pending view is discarded. Flush runs once
    // the active form control loses focus (or pointerup for a slider).
    pendingMount = { parent, view }
    return
  }
  mountImmediate(parent, view)
}

function mountImmediate(parent: HTMLElement, view: Node): void {
  // Focus + selection preservation across DOM swaps.
  //
  // Problem: iOS Safari dismisses the on-screen keyboard the moment the
  // focused <input> is detached from the DOM. Every parent.replaceChildren
  // call tears down whatever the user was typing into, so the keyboard
  // closes mid-keystroke and the user has to tap the input again to keep
  // going. Same class of bug for range sliders: a mid-drag rerender drops
  // pointer capture and the drag aborts.
  //
  // Fix: pages tag focusable elements with `data-focus-key="..."`. Before
  // the swap we record which key is focused + its selection range; after
  // the swap we find the new node with the matching key and restore focus.
  // The find-and-refocus runs synchronously in the same tick as the swap,
  // which iOS Safari treats as "focus moved" rather than "input lost",
  // keeping the keyboard visible.
  const active = document.activeElement as HTMLElement | null
  let focusKey: string | null = null
  let selStart: number | null = null
  let selEnd: number | null = null
  if (active !== null && parent.contains(active)) {
    focusKey = active.getAttribute('data-focus-key')
    if (active instanceof HTMLInputElement || active instanceof HTMLTextAreaElement) {
      try {
        selStart = active.selectionStart
        selEnd = active.selectionEnd
      } catch {
        // Some input types (range, checkbox) throw on selectionStart access.
      }
    }
  }

  // Scroll preservation across the DOM swap. replaceChildren rebuilds the
  // whole tree, so without this the page (and any inner overflow container
  // like the desktop players sidebar) snaps back to the top on every
  // rerender - and the voice page rerenders on every snapshot frame (~1 Hz)
  // plus the 10 Hz talking/peak poll, so a user who scrolls down can't read
  // anything before it yanks them up again. We capture the document scroll
  // offset plus the scrollTop of any element tagged `data-scroll-key`, then
  // restore both after the swap.
  const scroller = document.scrollingElement as HTMLElement | null
  const docTop = scroller?.scrollTop ?? 0
  const docLeft = scroller?.scrollLeft ?? 0
  const innerScrolls = new Map<string, { top: number; left: number }>()
  for (const node of parent.querySelectorAll<HTMLElement>('[data-scroll-key]')) {
    const key = node.getAttribute('data-scroll-key')
    if (key !== null) innerScrolls.set(key, { top: node.scrollTop, left: node.scrollLeft })
  }

  parent.replaceChildren(view)

  if (scroller !== null && (scroller.scrollTop !== docTop || scroller.scrollLeft !== docLeft)) {
    scroller.scrollTop = docTop
    scroller.scrollLeft = docLeft
  }
  if (innerScrolls.size > 0) {
    for (const node of parent.querySelectorAll<HTMLElement>('[data-scroll-key]')) {
      const key = node.getAttribute('data-scroll-key')
      if (key === null) continue
      const saved = innerScrolls.get(key)
      if (saved === undefined) continue
      node.scrollTop = saved.top
      node.scrollLeft = saved.left
    }
  }

  if (focusKey !== null) {
    const next = parent.querySelector<HTMLElement>(`[data-focus-key="${escapeAttrValue(focusKey)}"]`)
    if (next !== null) {
      try {
        next.focus({ preventScroll: true })
        if (
          selStart !== null && selEnd !== null &&
          (next instanceof HTMLInputElement || next instanceof HTMLTextAreaElement)
        ) {
          try { next.setSelectionRange(selStart, selEnd) } catch { /* range input has no selection */ }
        }
      } catch {
        // Focus restoration is best-effort; some browsers refuse on certain
        // element types or while the page is hidden.
      }
    }
  }
}

/**
 * CSS.escape isn't on older WebKit, and our keys never need full escaping
 * because we control them - we just need to be safe with the chars we use.
 * Backslash + quote + newline are the dangerous ones for an attribute
 * selector context.
 */
function escapeAttrValue(value: string): string {
  return value.replace(/\\/g, '\\\\').replace(/"/g, '\\"')
}
